import { Extension, createDocument, getHTMLFromFragment, type Editor } from "@tiptap/core"
import { ChangeSet, simplifyChanges, type TokenEncoder } from "@tiptap/pm/changeset"
import type { Fragment, Mark, Node as ProseMirrorNode } from "@tiptap/pm/model"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { StepMap } from "@tiptap/pm/transform"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

export type DescriptionChange = { fromA: number; toA: number; fromB: number; toB: number }

export type TaskDiffMentionLabel = (category: string, id: number, stateId: number | null) => string

type Token = number | string

type DiffSide = { doc: ProseMirrorNode; tokens: Token[]; text: string }

const WORD_CHAR = /[\p{L}\p{N}_]/u

const marksKeys = new WeakMap<readonly Mark[], string>()

function marksKey(marks: readonly Mark[]): string {
  let key = marksKeys.get(marks)
  if (key === undefined) {
    key = marks.map((mark) => mark.type.name + JSON.stringify(mark.attrs)).join(",")
    marksKeys.set(marks, key)
  }
  return key
}

// Подпись чипа редактор берёт из файлов задачи и упомянутых людей и задач — это не правка текста.
function mentionStateId(node: ProseMirrorNode): number | null {
  const raw: unknown = node.attrs.stateId
  return raw == null || raw === "" ? null : Number(raw)
}

function nodeAttrs(node: ProseMirrorNode) {
  if (node.type.name === "mention") {
    return { ...node.attrs, id: Number(node.attrs.id), stateId: mentionStateId(node), label: null }
  }
  // id абзаца сценария — служебный якорь: версия, собранная из текста, получает новые id, это не правка.
  if (node.type.name === "paragraph" && "id" in node.attrs) return { ...node.attrs, id: null }
  return node.attrs
}

const diffEncoder: TokenEncoder<Token> = {
  encodeCharacter: (char, marks) => (marks.length ? `${char}:${marksKey(marks)}` : char),
  encodeNodeStart: (node) => node.type.name + JSON.stringify(nodeAttrs(node)),
  encodeNodeEnd: (node) => `/${node.type.name}`,
  compareTokens: (a, b) => a === b,
}

function withoutTrailingEmptyBlocks(doc: ProseMirrorNode): ProseMirrorNode {
  let count = doc.childCount
  while (count > 1 && doc.child(count - 1).isTextblock && doc.child(count - 1).content.size === 0) count--
  if (count === doc.childCount) return doc
  let end = 0
  for (let index = 0; index < count; index++) end += doc.child(index).nodeSize
  return doc.copy(doc.content.cut(0, end))
}

function diffSide(doc: ProseMirrorNode): DiffSide {
  const tokens: Token[] = []
  let text = ""
  const walk = (fragment: Fragment) =>
    fragment.forEach((node) => {
      if (node.isText) {
        const value = node.text ?? ""
        for (let index = 0; index < value.length; index++) {
          tokens.push(diffEncoder.encodeCharacter(value.charCodeAt(index), node.marks))
        }
        text += value
        return
      }
      tokens.push(diffEncoder.encodeNodeStart(node))
      text += " "
      if (node.isLeaf) return
      walk(node.content)
      tokens.push(diffEncoder.encodeNodeEnd(node))
      text += " "
    })
  walk(doc.content)
  return { doc, tokens, text }
}

function isWordAt(text: string, pos: number): boolean {
  return pos >= 0 && pos < text.length && WORD_CHAR.test(text[pos])
}

function isWordBoundary(text: string, pos: number): boolean {
  return !(isWordAt(text, pos - 1) && isWordAt(text, pos))
}

function blockShift({ doc, tokens, text }: DiffSide, from: number, to: number, min: number, max: number): number {
  let low = 0
  while (from + low > min && tokens[from + low - 1] === tokens[to + low - 1]) low--
  if (doc.resolve(from).sameParent(doc.resolve(to))) {
    let high = 0
    while (to + high < max && tokens[from + high] === tokens[to + high]) high++
    let best: number | null = null
    for (let shift = low; shift <= high; shift++) {
      const fits =
        doc.resolve(from + shift).sameParent(doc.resolve(to + shift)) &&
        isWordBoundary(text, from + shift) &&
        isWordBoundary(text, to + shift)
      if (fits && (best === null || Math.abs(shift) < Math.abs(best))) best = shift
    }
    return best ?? 0
  }
  for (let shift = low; to + shift <= max; shift++) {
    if (doc.resolve(from + shift).sameParent(doc.resolve(to + shift))) return shift
    if (tokens[from + shift] !== tokens[to + shift]) break
  }
  return 0
}

// Вставку или удаление diff может начать с середины слова или соседнего абзаца — сдвигаем к границам слов и блоков.
function slideToBlocks(changes: readonly DescriptionChange[], a: DiffSide, b: DiffSide): DescriptionChange[] {
  const result: DescriptionChange[] = []
  changes.forEach((change, index) => {
    const prev = result[result.length - 1]
    const next = changes[index + 1]
    let shift = 0
    if (change.fromA === change.toA) {
      shift = blockShift(b, change.fromB, change.toB, prev?.toB ?? 0, next?.fromB ?? b.tokens.length)
    } else if (change.fromB === change.toB) {
      shift = blockShift(a, change.fromA, change.toA, prev?.toA ?? 0, next?.fromA ?? a.tokens.length)
    }
    result.push({
      fromA: change.fromA + shift,
      toA: change.toA + shift,
      fromB: change.fromB + shift,
      toB: change.toB + shift,
    })
  })
  return result
}

// simplifyChanges не трогает чистые вставки и удаления и замену одной буквы — их тоже доводим до целых слов.
function wholeWords(changes: readonly DescriptionChange[], a: DiffSide, b: DiffSide): DescriptionChange[] {
  const result: DescriptionChange[] = []
  changes.forEach((change, index) => {
    let { fromA, toA, fromB, toB } = change
    const prev = result[result.length - 1]
    const nextFromB = changes[index + 1]?.fromB ?? b.text.length
    const touchesStart = (fromA < toA && isWordAt(a.text, fromA)) || (fromB < toB && isWordAt(b.text, fromB))
    const touchesEnd = (fromA < toA && isWordAt(a.text, toA - 1)) || (fromB < toB && isWordAt(b.text, toB - 1))
    if (touchesStart) {
      while (fromB > (prev?.toB ?? 0) && isWordAt(b.text, fromB - 1)) {
        fromA--
        fromB--
      }
    }
    if (touchesEnd) {
      while (toB < nextFromB && isWordAt(b.text, toB)) {
        toA++
        toB++
      }
    }
    if (prev && fromB <= prev.toB) result[result.length - 1] = { fromA: prev.fromA, toA, fromB: prev.fromB, toB }
    else result.push({ fromA, toA, fromB, toB })
  })
  return result
}

export function descriptionChanges(base: ProseMirrorNode, doc: ProseMirrorNode): DescriptionChange[] {
  const a = diffSide(withoutTrailingEmptyBlocks(base))
  const b = diffSide(withoutTrailingEmptyBlocks(doc))
  const map = new StepMap([0, a.tokens.length, b.tokens.length])
  const { changes } = ChangeSet.create(a.doc, undefined, diffEncoder).addSteps(b.doc, [map], null)
  return wholeWords(slideToBlocks(simplifyChanges(changes, b.doc), a, b), a, b)
}

function hasInlineContent(doc: ProseMirrorNode, from: number, to: number): boolean {
  return doc.textBetween(from, to, "", "\ufffc") !== ""
}

function insertedRanges(base: ProseMirrorNode, doc: ProseMirrorNode, change: DescriptionChange): [number, number][] {
  const { fromA, toA, fromB, toB } = change
  if (fromB === toB) return []
  if (fromA === toA || hasInlineContent(base, fromA, toA) || hasInlineContent(doc, fromB, toB)) return [[fromB, toB]]
  // Сменился только вид блока (уровень заголовка, тип списка) — отмечаем его текст.
  const ranges: [number, number][] = []
  doc.nodesBetween(fromB, toB, (node, pos) => {
    if (pos < fromB) return true
    ranges.push([pos + 1, pos + node.nodeSize - 1])
    return false
  })
  return ranges
}

function deletedText(base: ProseMirrorNode, change: DescriptionChange, mentionLabel: TaskDiffMentionLabel): string {
  if (change.fromA === change.toA) return ""
  const text = base.textBetween(change.fromA, change.toA, "\n", (node) => {
    if (node.type.name === "mention") {
      return mentionLabel(String(node.attrs.category ?? "file"), Number(node.attrs.id), mentionStateId(node))
    }
    return node.type.name === "hardBreak" ? "\n" : ""
  })
  if (text.trim()) return text
  return text.includes(" ") ? " " : ""
}

function deletedWidget(doc: ProseMirrorNode, pos: number, text: string): Decoration {
  const block = !doc.resolve(pos).parent.inlineContent
  return Decoration.widget(
    pos,
    () => {
      const element = document.createElement("del")
      element.className = block ? "task-diff-deleted task-diff-deleted-block" : "task-diff-deleted"
      element.textContent = text
      return element
    },
    { side: -1, key: `deleted:${pos}:${text}` },
  )
}

export function descriptionDiffDecorations(
  base: ProseMirrorNode,
  doc: ProseMirrorNode,
  mentionLabel: TaskDiffMentionLabel,
): DecorationSet {
  const decorations: Decoration[] = []
  for (const change of descriptionChanges(base, doc)) {
    if (change.toA > base.content.size || change.toB > doc.content.size) continue
    for (const [from, to] of insertedRanges(base, doc, change)) {
      if (from < to) decorations.push(Decoration.inline(from, to, { class: "task-diff-inserted" }))
    }
    const text = deletedText(base, change, mentionLabel)
    if (text) decorations.push(deletedWidget(doc, change.fromB, text))
  }
  return DecorationSet.create(doc, decorations)
}

// Тот же разбор, что у редактора при создании, вместе с пересборкой через HTML из onCreate.
function descriptionDoc(editor: Editor, markdown: string): ProseMirrorNode {
  const json = editor.markdown?.parse(markdown)
  const doc = createDocument(json?.content?.length ? json : markdown, editor.schema, editor.options.parseOptions)
  try {
    doc.check()
    return doc
  } catch {
    return createDocument(getHTMLFromFragment(doc.content, editor.schema), editor.schema)
  }
}

type DiffBuilder = (doc: ProseMirrorNode) => DecorationSet

type DiffState = { build: DiffBuilder | null; decorations: DecorationSet }

const taskDescriptionDiffKey = new PluginKey<DiffState>("taskDescriptionDiff")

// Документ меняется и без правок (TrailingNode, подписи чипов, пересборка через HTML в onCreate) — пересчитываем.
export const TaskDescriptionDiff = Extension.create({
  name: "taskDescriptionDiff",
  addProseMirrorPlugins() {
    return [
      new Plugin<DiffState>({
        key: taskDescriptionDiffKey,
        state: {
          init: () => ({ build: null, decorations: DecorationSet.empty }),
          apply: (tr, value) => {
            const meta: DiffBuilder | null | undefined = tr.getMeta(taskDescriptionDiffKey)
            if (meta === undefined && (!tr.docChanged || !value.build)) return value
            const build = meta === undefined ? value.build : meta
            return { build, decorations: build ? build(tr.doc) : DecorationSet.empty }
          },
        },
        props: {
          decorations: (state) => taskDescriptionDiffKey.getState(state)?.decorations,
        },
      }),
    ]
  },
})

export function setTaskDescriptionDiff(editor: Editor, base: string | undefined, mentionLabel: TaskDiffMentionLabel) {
  setDescriptionDiffBase(editor, base === undefined ? null : descriptionDoc(editor, base), mentionLabel)
}

/** База сравнения — готовый документ (сценарий: драфт против текущего текста); null снимает подсветку. */
export function setDescriptionDiffBase(
  editor: Editor,
  baseDoc: ProseMirrorNode | null,
  mentionLabel: TaskDiffMentionLabel = () => "",
) {
  if (!baseDoc && !taskDescriptionDiffKey.getState(editor.state)?.build) return
  const build: DiffBuilder | null = baseDoc ? (doc) => descriptionDiffDecorations(baseDoc, doc, mentionLabel) : null
  editor.view.dispatch(editor.state.tr.setMeta(taskDescriptionDiffKey, build).setMeta("addToHistory", false))
}
